import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Styled components
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const LegendWrap = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  font-size: 12px;
  color: #444;
  margin-bottom: 6px;
`;
const Swatch = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${(p) => p.color || "#ccc"};
  margin-right: 6px;
`;

// colors
const GREEN  = "#2e7d32";
const ORANGE = "#fb8c00";

const AverageTransactionValueByRegionChart = ({ salesData = [], clientsData = [] }) => {
  const [chartData, setChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0); // 0 = Cały rok

  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie",
    ŚLĄSKIE: "śląskie",
    PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie",
    ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie",
    DOLNOŚLĄSKIE: "dolnośląskie",
    ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie",
    POMORSKIE: "pomorskie",
    LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie",
    PODLASKIE: "podlaskie",
    "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
    MAŁOPOLSKIE: "małopolskie",
  };

  // default to latest year
  useEffect(() => {
    if (!salesData.length) return;
    if (!selectedYear) {
      const latestYear = Math.max(...salesData.map((r) => parseInt(r.Rok)));
      setSelectedYear(latestYear);
    }
  }, [salesData, selectedYear]);

  useEffect(() => {
    if (!salesData.length || !clientsData.length || !selectedYear) return;

    // map NIP -> region (pretty)
    const nipToRegion = {};
    clientsData.forEach((client) => {
      const nip = client.nip?.trim();
      if (!nip) return;
      const regionRaw = client.wojewodztwo?.trim()?.toUpperCase();
      const mapped = regionNameMapping[regionRaw];
      nipToRegion[nip] = mapped || regionRaw || "Unknown";
    });

    // helper to compute totals & counts for a (year, month or full)
    const computeAgg = (year, monthOrZero) => {
      const totals = {};
      const counts = {};
      salesData.forEach((row) => {
        const rok = parseInt(row.Rok);
        const miesiac = parseInt(row.Miesiac);
        if (rok !== year) return;
        if (monthOrZero !== 0 && miesiac !== monthOrZero) return;

        const nip = row.NrKontrahenta?.trim();
        const region = nipToRegion[nip] || "Unknown";

        // sum revenue across known fields
        const revenue = ["K_15", "K_19", "K_10", "K_21", "K_17", "K_11", "K_22"].reduce(
          (sum, key) => sum + (parseFloat(row[key]) || 0),
          0
        );
        if (revenue === 0 || region === "Unknown") return;

        totals[region] = (totals[region] || 0) + revenue;
        counts[region] = (counts[region] || 0) + 1;
      });
      return { totals, counts };
    };

    const curr = computeAgg(selectedYear, selectedMonth);
    const prev = computeAgg(selectedYear - 1, selectedMonth);

    const regions = new Set([...Object.keys(curr.totals), ...Object.keys(prev.totals)]);
    const rows = [...regions].map((region) => {
      const cTot = curr.totals[region] || 0;
      const cCnt = curr.counts[region] || 0;
      const pTot = prev.totals[region] || 0;
      const pCnt = prev.counts[region] || 0;

      const avgCurrent = cCnt ? cTot / cCnt : 0;
      const avgPrev = pCnt ? pTot / pCnt : 0;

      return {
        region,
        avgTransaction: avgCurrent,
        avgTransactionPrev: avgPrev,
      };
    });

    // sort by current avg desc
    rows.sort((a, b) => b.avgTransaction - a.avgTransaction);
    setChartData(rows);
  }, [salesData, clientsData, selectedYear, selectedMonth]);

  const getMonthName = (num) =>
    new Date(2000, num - 1).toLocaleString("pl-PL", { month: "long" });

  const availableYears = [...new Set(salesData.map((r) => parseInt(r.Rok)))].sort((a, b) => b - a);
  const availableMonths = selectedYear
    ? [
        ...new Set(
          salesData
            .filter((r) => parseInt(r.Rok) === selectedYear)
            .map((r) => parseInt(r.Miesiac))
        ),
      ].sort((a, b) => a - b)
    : [];

  // custom tooltip with r/r details
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const m = payload.reduce((acc, p) => ({ ...acc, [p.dataKey]: p.value }), {});
    const curr = m.avgTransaction ?? 0;
    const prev = m.avgTransactionPrev ?? 0;
    const diff = curr - prev;
    const pct = prev === 0 ? null : (diff / prev) * 100;

    return (
      <div style={{ background: "#fff", border: "1px solid #ddd", padding: 10, fontSize: 12 }}>
        <div style={{ fontWeight: "bold", marginBottom: 6 }}>{label}</div>
        <div>Śr. transakcja (obecny): <strong>{curr.toLocaleString("pl-PL")}</strong></div>
        <div>Śr. transakcja (r-1): <strong>{prev.toLocaleString("pl-PL")}</strong></div>
        <div>Różnica: <strong>{diff.toLocaleString("pl-PL")}</strong></div>
        <div>% zmiany: <strong>{pct === null ? "—" : `${pct.toFixed(2)}%`}</strong></div>
      </div>
    );
  };

  // custom legend
  const CustomLegend = () => (
    <LegendWrap>
      <span><Swatch color={GREEN} />Obecny okres</span>
      <span><Swatch color={ORANGE} />Rok wcześniej</span>
    </LegendWrap>
  );

  return (
    <div style={{ margin: "20px auto", width: "90%" }}>
      <ChartTitle>
        Średnia Wartość Transakcji wg Województw (z porównaniem r/r)
        <InfoIconWrapper data-tooltip-id="avg-transactions-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="avg-transactions-tooltip"
        place="left"
        content={
          <>
            <strong>Średnia wartość transakcji</strong>
            <br />
            Porównanie {selectedMonth === 0 ? "całego roku" : "miesiąca"} {selectedYear} do {selectedYear - 1}.
          </>
        }
      />

      <Controls>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Rok:</label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            <option value={0}>Cały rok</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </Select>
        </div>
      </Controls>

      {/* legend that matches our palette */}
      <CustomLegend />

      {chartData.length === 0 ? (
        <p style={{ marginTop: "20px", color: "#888" }}>Brak danych do wyświetlenia.</p>
      ) : (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(val) => val.toLocaleString("pl-PL")} />
            <YAxis type="category" dataKey="region" width={150} />
            <Tooltip content={<CustomTooltip />} />

            {/* Current = GREEN (uniform) */}
            <Bar dataKey="avgTransaction" name="Obecny okres" barSize={28} fill={GREEN} />

            {/* Previous = ORANGE */}
            <Bar dataKey="avgTransactionPrev" name="Rok wcześniej" barSize={28} fill={ORANGE} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default AverageTransactionValueByRegionChart;
